import { useEffect } from "react";
import {
  useFeatureFlagEnabled,
  useFeatureFlagVariantKey,
} from "posthog-js/react";
import type { PostHogInterface } from "posthog-js";
import { bridge } from "./bridge";
import { recordOnboardingExperiment } from "./onboardingSession";

// First build number that ships the flutter_stripe Payment Sheet integration.
// Any native app build below this falls back to RevenueCat IAP regardless of
// the experiment flag.
const STRIPE_NATIVE_MIN_BUILD = 109;

export const forceParameterFeatureFlags = (posthog: PostHogInterface) => {
  const params = new URLSearchParams(window.location.search);
  const ffParam = params.get("ff");

  if (!ffParam) return;

  // Parse the feature flag overrides
  // Format: flag1:value1,flag2:value2
  const overrides: Record<string, string | boolean> = {};

  ffParam.split(",").forEach((pair) => {
    const [flag, value] = pair.split(":");
    if (flag && value !== undefined) {
      // Convert "true"/"false" strings to booleans
      if (value === "true") {
        overrides[flag.trim()] = true;
      } else if (value === "false") {
        overrides[flag.trim()] = false;
      } else {
        overrides[flag.trim()] = value.trim();
      }
    }
  });

  if (Object.keys(overrides).length > 0) {
    posthog.featureFlags.overrideFeatureFlags({ flags: overrides });
  }
}
/** Reads a single flag out of the `?ff=flag:value,...` QA override param. */
function forcedFlagValue(flag: string): string | undefined {
  const ff = new URLSearchParams(window.location.search).get("ff");
  if (!ff) return undefined;
  for (const pair of ff.split(",")) {
    const [key, value] = pair.split(":");
    if (key?.trim() === flag && value !== undefined) return value.trim();
  }
  return undefined;
}

// Feature flag hooks
export const useShowReferralSection = () => useFeatureFlagEnabled("show_referral_section");

// "+1" invite feature — gates the booking-card roster, the /invite/:code
// accept page, and the post-signin redirect that picks up stashed codes.
// PostHog flag key: show_plus_one_invites.
export const usePlusOneInvites = () => useFeatureFlagEnabled("plus_one");

export const useQuizPhoneOptional = () => {
  const inExperiment = useFeatureFlagVariantKey("quiz_phone_optional") === "experiment";
  return inExperiment && bridge.isNative;
};

// Native Stripe checkout — replaces RevenueCat IAP with flutter_stripe Payment Sheet.
// Both conditions must hold:
//   1. PostHog flag `native_stripe_checkout` is in the `experiment` variant
//   2. The native build number is at least STRIPE_NATIVE_MIN_BUILD (older builds
//      don't have the StripeService wired up and would silently fail)
// Returns false when running on web (no bridge → buildNumber is 0).
export const useNativeStripeEnabled = (): boolean => {
  return bridge.appBuildNumber >= STRIPE_NATIVE_MIN_BUILD;
};

// Guided post-signup booking — after the quiz + signup complete, users who
// came from a typed ad landing (?et=c coffee / ?et=d dinner, persisted to
// localStorage by persistAdEventTypeFromURL) are routed to /booking/start,
// an onboarding-styled screen that pre-picks the next event of that type
// instead of dropping them on the generic booking page.
// PostHog flag key: guided_post_signup_booking. Variants: control | experiment.
// Users without a stored ad type always get control behavior regardless of
// the flag (there's nothing to guide toward).
// QA: /app/quiz?ff=guided_post_signup_booking:experiment (then finish the quiz),
// or open /app/booking/start directly.
export const useGuidedPostSignupBooking = () =>
  useFeatureFlagVariantKey("guided_post_signup_booking") === "experiment";

// Google sign-in via signInWithRedirect instead of signInWithPopup. On mobile
// Safari, Google popups convert at ~75% against ~84% for Apple popups on the
// same browser (90d to 2026-08-15, n≈4.1k/6.1k clicks) — same popup mechanism,
// so what Google loses on top of Apple is the popup handshake compounding with
// its own extra steps. The redirect keeps the flow on the top-level page and
// never opens one.
//
// Note the gap is ~9 points, not the ~14 that docs/FUNNEL_OPTIMIZATION.md (E6)
// quotes: that read was 30d and Apple's number has come down since. Sizing the
// expected lift off 88% would over-promise.
//
// PostHog flag key: google_auth_redirect.
// Variants: control (popup) | experiment (redirect).
//
// Mobile browsers only: the popup works fine on desktop, and a redirect there
// would trade a working flow for a full page reload. Native apps always use the
// bridge flow regardless of the flag.
//
// The arm only reaches surfaces that can survive their own page being
// destroyed — see `allowRedirect` on SignInForm. Target the flag to mobile in
// PostHog too, otherwise the desktop users filtered out here still land in the
// denominator on both sides and flatten the measured effect.
// QA: append ?ff=google_auth_redirect:experiment on a mobile browser (or
// device emulation with a mobile user agent). Read through `forcedFlagValue`
// as well as PostHog, so the arm is reachable in dev, where PostHog isn't
// initialized and the override it normally installs never lands.
export const useGoogleRedirectSignIn = () => {
  const variant = useFeatureFlagVariantKey("google_auth_redirect");
  const forced = forcedFlagValue("google_auth_redirect");
  const inExperiment = (forced ?? variant) === "experiment";
  return inExperiment && !bridge.isNative && isMobileBrowser();
};

/**
 * A browser where popups are the unreliable option — phones and tablets.
 *
 * The user agent is the primary signal; iPadOS 13+ reports a desktop UA, so a
 * coarse pointer with real touch points backs it up. Read at call time rather
 * than cached: nothing here changes within a session, but a helper that lies
 * after a device-emulation toggle makes QA harder than it needs to be.
 */
function isMobileBrowser(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  if (/Android|iPhone|iPad|iPod|Mobile|Silk/i.test(navigator.userAgent)) {
    return true;
  }
  return (
    navigator.maxTouchPoints > 1 &&
    window.matchMedia?.("(pointer: coarse)").matches === true
  );
}

// One-time email code sign-in — the email path mails a 6-digit code instead of
// asking for a password. Handled entirely in the web client; inside the app the
// WebView hands the Flutter shell a Firebase custom token afterwards.
// PostHog flag key: email_code_auth.
// Variants: control (password first) | experiment (code first).
// Both variants keep the *other* path reachable via an escape-hatch link, so
// existing password accounts are never stranded and QA can compare the two
// without flipping the flag.
// QA: append ?ff=email_code_auth:experiment
export const useEmailCodeSignIn = () =>
  useFeatureFlagVariantKey("email_code_auth") === "experiment";

// Host-created custom events — gates the entire host surface (create page,
// "my events" links, anything else added later). Public /events/{slug} pages stay
// ungated so share links keep working for recipients without the flag.
// Returns undefined while flags are loading, then a boolean.
export const useCustomEvents = (): boolean | undefined =>
  useFeatureFlagEnabled("custom_events");

// No-show fee disclaimer on the booking screen — a short notice next to
// the CTA saying a no-show fee applies. Bookings made with it on screen are stamped
// `noShowDisclaimerAccepted`, which is what makes them chargeable (see
// docs/NO_SHOW_FEES.md); bookings made without it never are.
// PostHog flag key: no_show_disclaimer. Variants: control | experiment.
// Judged on booking-screen → booking conversion, since the only thing it can
// cost is people who balk at the fee.
// QA: /app/booking?ff=no_show_disclaimer:experiment
export const useNoShowDisclaimer = () => {
  const variant = useFeatureFlagVariantKey("no_show_disclaimer");
  return (forcedFlagValue("no_show_disclaimer") ?? variant) === "experiment";
};

// Money-back guarantee window — the guarantee copy on the two purchase
// surfaces (the checkout sheet in PaymentModalV2 and the onboarding
// PlansScreen) promises a refund within 14 days. This arm shortens the
// promise to 7. Copy only: the day count is interpolated into the same
// locale strings, nothing else on the surface changes.
// PostHog flag key: guarantee_window. Variants: control (14) | experiment (7).
// Judged on purchase conversion, with refund rate as the guardrail. Support
// has to honor whichever window the buyer was shown; the arm is recorded on
// the onboarding session so a purchase can be traced back to it.
// Falls back to control while flags are loading rather than holding a
// revenue-critical screen on the flag request.
// QA: /app/booking?ff=guarantee_window:experiment
export const GUARANTEE_DAYS_CONTROL = 14;
export const GUARANTEE_DAYS_EXPERIMENT = 7;
export const useGuaranteeDays = (): number => {
  const variant = useFeatureFlagVariantKey("guarantee_window");
  const resolved = forcedFlagValue("guarantee_window") ?? variant;
  useEffect(() => {
    if (typeof resolved === "string") {
      recordOnboardingExperiment("guarantee_window", resolved);
    }
  }, [resolved]);
  return resolved === "experiment"
    ? GUARANTEE_DAYS_EXPERIMENT
    : GUARANTEE_DAYS_CONTROL;
};
